import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod } from '@api-new/authservice';
import { ENTRY_FLOW } from '@app/sign-up/constants/entry-flow';
import { getFlowStep } from '@app/sign-up/utils/get-flow-step';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { AppState } from '@shared/models/app-state.model';
import { ENUM_SignUpCreationMethodRecord } from '@shared/records/enum-sign-up-creation-method.record';
import { AnalyticsService } from '@shared/services/analytics.service';
import { LocalStorageSyncService } from '@shared/services/local-storage-sync.service';
import {
  loadSignUpFlowFromStorage,
  loadSignUpFlowFromStorageSuccess,
  setSignUpFlowCurrentStep,
  setSignUpFlowData,
  setSignUpFlowInitialStep,
  setSignUpFlowState,
  signUpFlowNextStep,
  signUpFlowPreviousStep,
} from '@shared/store/sign-up/flow/flow.actions';
import { selectSignUpFlowState } from '@shared/store/sign-up/flow/flow.selectors';
import { signUpFlowSelectorName } from '@shared/store/sign-up/flow/models/sign-up-flow-selector-name';
import { getNextStep } from '@shared/store/sign-up/flow/utils/get-next-step';
import { getPreviousStep } from '@shared/store/sign-up/flow/utils/get-previous-step';
import { selectSignUpState } from '@shared/store/sign-up/sign-up.selectors';
import { take, tap, timer, withLatestFrom } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable()
export class SignUpFlowEffects {
  private readonly store: Store<AppState> = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly localStorageSyncService = inject(LocalStorageSyncService);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly analyticsService = inject(AnalyticsService);

  loadFlowFromStorage = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSignUpFlowFromStorage),
      map(() => loadSignUpFlowFromStorageSuccess({ state: this.localStorageSyncService.loadFromStorage(signUpFlowSelectorName) })),
    ),
  );

  saveFlowToStorage = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSignUpFlowState, setSignUpFlowData, setSignUpFlowInitialStep, setSignUpFlowCurrentStep),
        withLatestFrom(this.store.select(selectSignUpFlowState)),
        tap(([_, flowState]) => {
          this.localStorageSyncService.saveToStorage(signUpFlowSelectorName, flowState);
        }),
      ),
    { dispatch: false },
  );

  flowPreviousStep = createEffect(() =>
    this.actions$.pipe(
      ofType(signUpFlowPreviousStep),
      withLatestFrom(this.store.select(selectSignUpState)),
      filter(
        ([_, appState]) => appState.flow.currentStep !== appState.flow.initialStep && appState.flow.currentStep !== ENTRY_FLOW.Landing,
      ),
      map(([_, appState]) => {
        return setSignUpFlowCurrentStep({ step: getPreviousStep(appState) });
      }),
    ),
  );

  flowNextStep = createEffect(() =>
    this.actions$.pipe(
      ofType(signUpFlowNextStep),
      withLatestFrom(this.store.select(selectSignUpState)),
      map(([_, appState]) => {
        return setSignUpFlowCurrentStep({ step: getNextStep(appState) });
      }),
    ),
  );

  flowRouter = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSignUpFlowCurrentStep, loadSignUpFlowFromStorageSuccess, setSignUpFlowState),
        withLatestFrom(this.store.select(selectSignUpState)),
        switchMap(([_, signUpState]) =>
          timer(0, 50).pipe(
            filter(() => this.router.navigated),
            take(1),
            map(() => {
              const flowState = signUpState.flow;
              if (location.pathname.includes(paths.signUp.WelcomeEmail) || location.pathname.includes(paths.signUp.SignUpError)) return;

              const currentStep = getFlowStep(flowState.entity.creationMethod, flowState.currentStep);
              if (currentStep == null) {
                this.resetFlow();
                return;
              }

              const currentPath = this.router.url.split('/').at(-1);
              if (currentStep.page.route === currentPath) {
                // Everything is fine
                this.router
                  .navigate([], {
                    queryParamsHandling: 'merge',
                    queryParams: { page: this.getPageQueryParam(flowState.entity.creationMethod, currentStep.name) },
                  })
                  .then(() => {
                    this.setPageTitle(flowState.entity.creationMethod, currentStep.name);
                  });
              } else {
                // Navigate to correct page
                void this.router
                  .navigate(['/', paths.SIGN_UP, currentStep.page.route], {
                    queryParamsHandling: 'merge',
                    queryParams: { page: this.getPageQueryParam(flowState.entity.creationMethod, currentStep.name) },
                  })
                  .then(() => {
                    this.setPageTitle(flowState.entity.creationMethod, currentStep.name);
                  });
              }
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  private resetFlow(): void {
    this.store.dispatch(setSignUpFlowCurrentStep({ step: ENTRY_FLOW.Landing }));
    void this.router.navigate([paths.signUp.Entry], { queryParamsHandling: 'preserve' });
  }

  private getPageQueryParam(creationMethodName: HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod | null, stepName: string): string | null {
    return `${creationMethodName ?? 'ENTRY'}_${stepName}`;
  }

  private setPageTitle(creationMethodName: HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod | null, stepName: string): void {
    const flowTitle = creationMethodName != null ? ENUM_SignUpCreationMethodRecord[creationMethodName] : 'Entry';
    const stepTitle = this.splitStepName(stepName);
    this.titleService.setTitle(`Sign Up - ${flowTitle} - ${stepTitle}`);
  }

  private splitStepName(input: string): string {
    const words = input.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g);
    return words?.join(' ') ?? 'Unknown';
  }
}
